import React, { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Menu } from 'semantic-ui-react';
import { Link } from 'gatsby';
import { Icon } from 'src/components/shared';
import { findIndex, head, kebabCase } from 'lodash';
import classNames from 'classnames';

import Selectors from 'src/state/root-selectors';
import { connect, ConnectedProps } from 'react-redux';
import { IState } from '@pvolve/sdk/src/redux/selectors';

import { stripTrailingSlash } from 'src/utils/route-utils';

import * as Styles from 'src/styles/header.module.scss';
import withLocation from 'src/components/withLocation';

import InspirationalQuote from '../inspirational-quote';
import { useDeviceDetect } from 'src/utils/breakpoint-utils';
import { useBreakpoints, Breakpoints, Directions } from 'src/utils/breakpoint-utils';

import HeaderLogo from './HeaderLogo';
import HeaderMenu from './HeaderMenu';
import { useIsFTU } from 'src/components/my-plan/MyPlanContext';
import useNavItems from 'src/hooks/useNavItems';

const connector = connect((state: IState) => ({
    entitled: Selectors.auth.entitled(state),
    loggedIn: Selectors.auth.loggedIn(state),
    shopifyUrl: Selectors.config.shopifyUrl(state),
    experienceLoading: Selectors.account.loading(state),
}));

interface HeaderProps {
    path: string;
}

const Header = ({
    entitled,
    loggedIn,
    shopifyUrl,
    location,
    experienceLoading,
    path: currentPath,
}: HeaderProps & ConnectedProps<typeof connector>) => {
    const isFTU = useIsFTU();

    const navItems = useNavItems();

    const submenuTimer = useRef<NodeJS.Timeout>();
    const isMounted = useRef<boolean>();

    const isAccount = !!location.pathname.match(/account/gi);
    const isMyPlan = !!location.pathname.match(/my-plan/gi);

    const device = useDeviceDetect();
    const isMobile = device?.type === 'mobile';

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    let activePathIndex = entitled ? 0 : 1;

    const [displaySubnav, setDisplaySubnav] = useState(!isAccount);

    /**
     * A navIndex refers to an integer index into the `NAV_ITEMS` array
     */
    const [activeNavIndex, setActiveNavIndex] = useState<number | null>(null);

    /**
     * a subnavKey refers to a string identifer of a subnav item
     * formatted as `<parent_nav_item_label>-<integer_index_into_parent_subnav_array>`
     */
    const [activeSubnavKey, setActiveSubnavKey] = useState<string | null>(null);

    /**
     * Returns true if the supplied pathname string matches the current
     * application pathname.
     *
     * @param {String} pathname
     */
    const isCurrentPath = (pathname: string) =>
        stripTrailingSlash(pathname) === stripTrailingSlash(currentPath);

    /**
     * Generate a unique key for a subnav item
     * using the parent nav item's label
     * and the index in the parents subnav
     *
     * @param {Object} navItem
     * @param {Integer} index
     * @returns {String}
     */
    const getSubnavKey = (parentNavItem, index) => `${kebabCase(parentNavItem.label)}-${index}`;

    const handleNavItemMouseOver = (e: React.SyntheticEvent<EventTarget>) => {
        const targetNavItem = e.target.nodeName === 'I' ? e.target.parentElement : e.target;
        const targetNavIndex = parseInt(targetNavItem.dataset.navIndex);

        setActiveNavIndex(targetNavIndex);
        setDisplaySubnav(true);
        cancelSubmenuTimer();
    };

    const cancelSubmenuTimer = useCallback(() => {
        if (isMounted.current && submenuTimer.current) {
            clearTimeout(submenuTimer.current);
        }
    }, [isMounted, submenuTimer]);

    const handleNavItemMouseOut = useCallback(() => {
        submenuTimer.current = setTimeout(() => {
            if (isMounted.current) {
                setActiveNavIndex(isAccount ? null : activePathIndex);
                setDisplaySubnav(!isAccount);
            }
        }, 500);
    }, [activePathIndex, isMounted]);

    const handleSubnavItemClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;

        const targetSubnavKey = target.dataset.subnavKey;
        const targetParentNavIndex = target.dataset.parentNavIndex;

        setActiveSubnavKey(targetParentNavIndex);

        if (targetSubnavKey !== activeSubnavKey) {
            setActiveSubnavKey(targetSubnavKey);
        }

        setDisplaySubnav(true);
    };

    const navLink = (navItem) => {
        if (navItem?.pathname) {
            /**
             * Gatsby internal pages should use the Gatsby `Link` Component
             */
            return {
                as: Link,
                to: navItem.pathname,
            };
        }
        if (navItem?.url) {
            /**
             * External pages (i.e., on shopify) uses `a` tags
             */
            return {
                as: 'a',
                href: navItem.url,
            };
        }
    };

    const getNavItemProps = (navItem, isSubnavItem = false) => {
        let active;
        const navItemProps = navLink(head(navItem?.subnav) || navItem);

        if (isSubnavItem) {
            /**
             * Use the parent nav item to determine the subnav key
             * subnavKey is of the format `<kebab-case-parent-label>-<integer-index>`
             */
            const parentNavItem = navItems[activeNavIndex];
            const subnavKey = getSubnavKey(parentNavItem, findIndex(parentNavItem.subnav, navItem));

            active =
                subnavKey === activeSubnavKey ||
                (!!navItem.pathname?.match('workouts') && !!currentPath.match(/workout/gi)) ||
                (!!navItem.pathname?.match('series') && !!currentPath.match(/series/gi));
        } else {
            active = findIndex(navItems, navItem) === activeNavIndex;
        }
        return {
            active,
            ...navItemProps,
        };
    };

    const menuItem = (navItem, index: number) => (
        <Menu.Item
            key={index}
            onMouseOver={navItem.subnav && handleNavItemMouseOver}
            onMouseOut={handleNavItemMouseOut}
            data-nav-index={index}
            data-nav-pathname={navItem.pathname}
            {...getNavItemProps(navItem)}
        >
            {navItem.label}
            {navItem.subnav && (
                <Icon size={11} name="pv-chevron-down" className={Styles.headerNavIcon} />
            )}
        </Menu.Item>
    );

    const subMenuItem = (subnavItem, index: number) => (
        <Menu.Item
            name={subnavItem.label}
            key={index}
            onClick={handleSubnavItemClick}
            data-parent-nav-index={activeNavIndex}
            data-pathname={subnavItem.pathname}
            data-subnav-key={getSubnavKey(navItems[activeNavIndex], index)}
            {...getNavItemProps(subnavItem, true)}
        />
    );

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        navItems.forEach((navItem, navIndex) => {
            /**
             * If main nav has a pathname that matches the current path
             * then mark it as active
             */
            if (navItem.pathname && isCurrentPath(navItem.pathname)) {
                if (navIndex !== activeNavIndex) {
                    activePathIndex = navIndex;
                    setActiveNavIndex(navIndex);
                }
            } else if (navItem.subnav) {
                /**
                 * If sub nav has a pathname that matches the current path
                 * then mark BOTH the main nav and sub nav items as active
                 */
                for (const subnavItem of navItem.subnav) {
                    if (subnavItem.pathname && isCurrentPath(subnavItem.pathname)) {
                        activePathIndex = navIndex;
                        const subnavKey = getSubnavKey(
                            navItem,
                            findIndex(navItem.subnav, subnavItem)
                        );

                        if (navIndex !== activeNavIndex && !isAccount) {
                            setActiveNavIndex(navIndex);
                        }

                        if (subnavKey !== activeSubnavKey) {
                            setActiveSubnavKey(subnavKey);
                        }

                        if (!displaySubnav) {
                            setDisplaySubnav(true);
                        }

                        return;
                    }
                }
            }
        });
        !isAccount && handleNavItemMouseOut();
    }, []);

    const bannerClassNames = classNames(
        Styles.banner,
        isFTU ? Styles.largeBanner : Styles.smallBanner
    );

    const isDesktop = useBreakpoints(Breakpoints.xxl, Directions.up);
    const isTabletLandscape = useBreakpoints(Breakpoints.lg, Directions.up) && !isDesktop;

    const topHeaderDesktopLayout = (
        <div className={Styles.headerNavTop}>
            <HeaderLogo entitled={entitled} shopifyUrl={shopifyUrl} />
            <Menu pointing text className={Styles.mainNav}>
                {navItems.map(menuItem)}
            </Menu>
            <HeaderMenu noSeriesNoExp={isFTU} />
        </div>
    );

    const topHeaderTabletLandscapeLayout = (
        <div className={Styles.headerNavTop}>
            <div className={Styles.headerNavTopLeft}>
                <HeaderLogo entitled={entitled} shopifyUrl={shopifyUrl} />
                <Menu pointing text className={Styles.mainNav}>
                    {navItems.map(menuItem)}
                </Menu>
            </div>
            <HeaderMenu noSeriesNoExp={isFTU} />
        </div>
    );

    const topHeaderLayout = isTabletLandscape
        ? topHeaderTabletLandscapeLayout
        : topHeaderDesktopLayout;

    return (
        <div className={Styles.headerNav}>
            {topHeaderLayout}
            <div className="padding--0">
                {device && !isMobile && isMyPlan && !experienceLoading && !isFTU && (
                    // ⚠️ Currently there are two separate banners! This one and the one inside of ../mobile/index
                    // ⚠️ Make updates in both locations!
                    <div className={bannerClassNames}>
                        <InspirationalQuote>
                            {(quoteText) => (
                                <h1 className="huge">
                                    <span className="upper bold display--block">
                                        {quoteText.headline}
                                    </span>
                                    <span className="accent lower">{quoteText.subheading}</span>
                                </h1>
                            )}
                        </InspirationalQuote>
                    </div>
                )}

                {displaySubnav && (
                    <Menu
                        pointing
                        text
                        className={isFTU && isMyPlan ? Styles.subNavFtu : Styles.subNav}
                        onMouseOver={cancelSubmenuTimer}
                        onMouseOut={handleNavItemMouseOut}
                    >
                        {activeNavIndex !== null &&
                            navItems[activeNavIndex].subnav &&
                            navItems[activeNavIndex].subnav?.map(subMenuItem)}
                    </Menu>
                )}
            </div>
        </div>
    );
};

export default connector(withLocation(Header));
